import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  TrendingDown,
  TrendingUp,
  Calendar,
  MapPin,
  Bell,
  Settings,
  BarChart3,
  LineChart,
  Info,
  X,
  Check,
  AlertTriangle,
  Lightbulb,
  Target,
  Loader2,
  Sun,
  Cloud,
  Snowflake,
  Flower2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Spinner } from "../components/ui/Spinner";
import { useRequireAuth } from "../hooks/useAuth";
import { useSeasonalityStore } from "../stores/seasonalityStore";
import {
  useSeasonalityMetrics,
  useSeasonalityAlerts,
  useSeasonalityPreferences,
  useUpdateSeasonalityPreferences,
  useDismissAlert,
  useAllRegionsMetrics,
} from "../hooks/useSeasonality";
import { useFilterOptions } from "../hooks/useOperators";
import { cn } from "../lib/utils";
import type { SeasonalityDashboardItem, SeasonalityAlert } from "../types/database";

const monthNames = [
  "Gen", "Feb", "Mar", "Apr", "Mag", "Giu",
  "Lug", "Ago", "Set", "Ott", "Nov", "Dic"
];

const seasonIcons = {
  winter: Snowflake,
  spring: Flower2,
  summer: Sun,
  autumn: Cloud,
};

function getSeasonFromMonth(month: number): keyof typeof seasonIcons {
  if (month >= 3 && month <= 5) return "spring";
  if (month >= 6 && month <= 8) return "summer";
  if (month >= 9 && month <= 11) return "autumn";
  return "winter";
}

export function Seasonality() {
  const { isLoading: authLoading } = useRequireAuth();
  const { regions } = useFilterOptions();

  // Store state
  const {
    selectedRegion,
    setSelectedRegion,
    selectedYear,
    setSelectedYear,
    chartView,
    setChartView,
    activeAlerts,
    showAlertPanel,
    setShowAlertPanel,
  } = useSeasonalityStore();

  // Local state
  const [showPreferences, setShowPreferences] = useState(false);

  // Queries
  const { data: metrics = [], isLoading: metricsLoading } = useSeasonalityMetrics(
    selectedRegion || undefined,
    selectedYear
  );
  const { data: allRegionsMetrics = [] } = useAllRegionsMetrics();
  const { data: alerts = [] } = useSeasonalityAlerts();
  const { data: preferences } = useSeasonalityPreferences();

  // Mutations
  const updatePreferences = useUpdateSeasonalityPreferences();
  const dismissAlert = useDismissAlert();

  // Computed data
  const regionStats = useMemo(() => {
    if (!selectedRegion || metrics.length === 0) return null;

    const avgDemand = metrics.reduce((sum, m) => sum + (m.estimated_demand_index || 0), 0) / metrics.length;
    const lowSeasonMonths = metrics.filter(m => m.is_low_season);
    const highSeasonMonths = metrics.filter(m => m.demand_level === "high");
    const bestMonth = metrics.reduce((best, m) =>
      (m.estimated_demand_index || 0) > (best?.estimated_demand_index || 0) ? m : best
      , metrics[0]);
    const worstMonth = metrics.reduce((worst, m) =>
      (m.estimated_demand_index || 0) < (worst?.estimated_demand_index || 0) ? m : worst
      , metrics[0]);

    return {
      avgDemand,
      lowSeasonMonths,
      highSeasonMonths,
      bestMonth,
      worstMonth,
    };
  }, [selectedRegion, metrics]);

  // Group metrics by region for heatmap
  const regionHeatmap = useMemo(() => {
    const grouped: Record<string, SeasonalityDashboardItem[]> = {};
    allRegionsMetrics.forEach(m => {
      if (!grouped[m.region]) grouped[m.region] = [];
      grouped[m.region].push(m);
    });
    return grouped;
  }, [allRegionsMetrics]);

  // Available years
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        >
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <TrendingDown className="h-6 w-6 text-amber-500" />
              Destagionalizzazione
            </h1>
            <p className="text-muted-foreground mt-1">
              Analizza i periodi a bassa domanda e pianifica strategie promozionali
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Alerts Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAlertPanel(!showAlertPanel)}
              className="relative"
            >
              <Bell className="h-4 w-4 mr-2" />
              Avvisi
              {activeAlerts.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                  {activeAlerts.length}
                </span>
              )}
            </Button>

            {/* Preferences Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPreferences(true)}
            >
              <Settings className="h-4 w-4 mr-2" />
              Preferenze
            </Button>
          </div>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select
            value={selectedRegion || ""}
            onChange={(e) => setSelectedRegion(e.target.value || null)}
            className="h-10 rounded-lg border border-border bg-card px-3 text-sm min-w-[180px] focus:border-amber-500 focus:outline-none"
          >
            <option value="">Tutte le regioni</option>
            {regions.map((region: string) => (
              <option key={region} value={region}>{region}</option>
            ))}
          </select>

          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="h-10 rounded-lg border border-border bg-card px-3 text-sm focus:border-amber-500 focus:outline-none"
          >
            {years.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>

          <div className="flex rounded-lg border border-border overflow-hidden">
            {[
              { id: "bar" as const, icon: BarChart3, label: "Barre" },
              { id: "line" as const, icon: LineChart, label: "Linea" },
            ].map((view) => (
              <button
                key={view.id}
                onClick={() => setChartView(view.id)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-2 text-sm transition-colors",
                  chartView === view.id
                    ? "bg-amber-500 text-white"
                    : "bg-card text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                )}
              >
                <view.icon className="h-4 w-4" />
                {view.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stats Overview */}
        {selectedRegion && regionStats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <BarChart3 className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{regionStats.avgDemand.toFixed(0)}%</p>
                      <p className="text-xs text-muted-foreground">Domanda Media</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                      <TrendingDown className="h-5 w-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{regionStats.lowSeasonMonths.length}</p>
                      <p className="text-xs text-muted-foreground">Mesi Bassa Stagione</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-emerald-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{monthNames[regionStats.bestMonth?.month - 1] || "-"}</p>
                      <p className="text-xs text-muted-foreground">Miglior Mese</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                      <Target className="h-5 w-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{monthNames[regionStats.worstMonth?.month - 1] || "-"}</p>
                      <p className="text-xs text-muted-foreground">Opportunità</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart Area */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  {chartView === "bar" ? (
                    <BarChart3 className="h-4 w-4 text-amber-500" />
                  ) : (
                    <LineChart className="h-4 w-4 text-amber-500" />
                  )}
                  Indice di Domanda {selectedYear}
                  {selectedRegion && <Badge variant="secondary">{selectedRegion}</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {metricsLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <Spinner size="lg" />
                  </div>
                ) : selectedRegion && metrics.length > 0 ? (
                  <SeasonalityChart metrics={metrics} chartType={chartView} />
                ) : !selectedRegion ? (
                  <RegionHeatmap regionHeatmap={regionHeatmap} onSelectRegion={setSelectedRegion} />
                ) : (
                  <div className="flex flex-col items-center justify-center h-64">
                    <BarChart3 className="h-12 w-12 text-muted-foreground opacity-30 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Nessun dato disponibile</h3>
                    <p className="text-muted-foreground text-sm">
                      I dati per questa regione vengono calcolati periodicamente
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Suggestions */}
            {selectedRegion && regionStats && regionStats.lowSeasonMonths.length > 0 && (
              <Card className="mt-6 border-amber-500/30 bg-amber-500/5">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-amber-500" />
                    Suggerimenti per {selectedRegion}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-background border border-border">
                      <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                        <Calendar className="h-4 w-4 text-amber-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-sm">Periodi Consigliati</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Pianifica attività promozionali per: {" "}
                          <span className="font-medium text-foreground">
                            {regionStats.lowSeasonMonths.map(m => monthNames[m.month - 1]).join(", ")}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        { icon: Target, title: "Crea Pacchetti Speciali", desc: "Offri pacchetti a prezzo ridotto per i periodi di bassa stagione" },
                        { icon: Info, title: "Promuovi Eventi Locali", desc: "Organizza eventi tematici per attirare visitatori" },
                      ].map((suggestion, i) => (
                        <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-background border border-border">
                          <suggestion.icon className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <h5 className="font-medium text-sm">{suggestion.title}</h5>
                            <p className="text-xs text-muted-foreground">{suggestion.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Active Alerts */}
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bell className="h-4 w-4 text-amber-500" />
                  Avvisi Attivi
                </CardTitle>
                {activeAlerts.length > 0 && (
                  <Badge variant="secondary">{activeAlerts.length}</Badge>
                )}
              </CardHeader>
              <CardContent>
                {alerts.length > 0 ? (
                  <div className="space-y-2">
                    {alerts.slice(0, 5).map((alert) => (
                      <AlertCard
                        key={alert.id}
                        alert={alert}
                        onDismiss={() => dismissAlert.mutate(alert.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Bell className="h-10 w-10 text-muted-foreground opacity-30 mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">Nessun avviso</h3>
                    <p className="text-muted-foreground text-sm">
                      Configura le preferenze per ricevere avvisi
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Monthly Breakdown */}
            {selectedRegion && metrics.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Dettaglio Mensile</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {metrics.map((m) => {
                      const season = getSeasonFromMonth(m.month);
                      const SeasonIcon = seasonIcons[season];
                      return (
                        <div
                          key={m.month}
                          className={cn(
                            "flex items-center gap-3 p-2 rounded-lg transition-colors",
                            m.is_low_season && "bg-red-500/10 border border-red-500/20"
                          )}
                        >
                          <SeasonIcon className={cn(
                            "h-4 w-4",
                            season === "summer" && "text-amber-500",
                            season === "winter" && "text-blue-400",
                            season === "spring" && "text-emerald-500",
                            season === "autumn" && "text-orange-500"
                          )} />
                          <span className="text-sm font-medium w-12">{monthNames[m.month - 1]}</span>
                          <div className="flex-1">
                            <div className="h-2 rounded-full bg-secondary overflow-hidden">
                              <div
                                className={cn(
                                  "h-full rounded-full transition-all",
                                  m.demand_level === "high" && "bg-emerald-500",
                                  m.demand_level === "medium" && "bg-amber-500",
                                  m.demand_level === "low" && "bg-red-500"
                                )}
                                style={{ width: `${m.estimated_demand_index}%` }}
                              />
                            </div>
                          </div>
                          <span className={cn(
                            "text-xs font-medium w-10 text-right",
                            m.demand_level === "high" && "text-emerald-500",
                            m.demand_level === "medium" && "text-amber-500",
                            m.demand_level === "low" && "text-red-500"
                          )}>
                            {m.estimated_demand_index?.toFixed(0)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Legend */}
            <Card>
              <CardContent className="p-4">
                <h4 className="text-sm font-medium mb-3">Legenda</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span className="text-muted-foreground">Alta domanda (&gt;70%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                    <span className="text-muted-foreground">Media domanda (40-70%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-muted-foreground">Bassa domanda (&lt;40%)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Preferences Modal */}
        <AnimatePresence>
          {showPreferences && (
            <PreferencesModal
              preferences={preferences}
              regions={regions}
              onSave={(updates) => {
                updatePreferences.mutate(updates);
                setShowPreferences(false);
              }}
              onClose={() => setShowPreferences(false)}
              isSaving={updatePreferences.isPending}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function SeasonalityChart({
  metrics,
  chartType,
}: {
  metrics: SeasonalityDashboardItem[];
  chartType: "bar" | "line";
}) {
  const chartHeight = 200; // Chart area height in pixels

  // Calculate SVG points for line chart - coordinates within the chart area
  const linePoints = useMemo(() => {
    if (chartType !== "line" || metrics.length === 0) return "";
    const segmentWidth = 100 / metrics.length;
    return metrics.map((m, i) => {
      const x = (i * segmentWidth) + (segmentWidth / 2);
      const y = 100 - (m.estimated_demand_index || 0); // Direct percentage mapping
      return `${x},${y}`;
    }).join(" ");
  }, [metrics, chartType]);

  // Points for the filled area polygon
  const areaPoints = useMemo(() => {
    if (chartType !== "line" || metrics.length === 0) return "";
    const segmentWidth = 100 / metrics.length;
    const firstX = segmentWidth / 2;
    const lastX = 100 - (segmentWidth / 2);
    return `${firstX},100 ${linePoints} ${lastX},100`;
  }, [linePoints, chartType, metrics.length]);

  return (
    <div className="w-full">
      {/* Chart container with Y-axis */}
      <div className="flex">
        {/* Y-axis labels */}
        <div className="w-10 flex flex-col justify-between text-xs text-muted-foreground pr-2" style={{ height: chartHeight }}>
          <span className="text-right">100%</span>
          <span className="text-right">75%</span>
          <span className="text-right">50%</span>
          <span className="text-right">25%</span>
          <span className="text-right">0%</span>
        </div>

        {/* Chart area */}
        <div className="flex-1 relative" style={{ height: chartHeight }}>
          {/* Grid lines */}
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="border-t border-dashed border-border/50" />
            ))}
          </div>

          {/* SVG for line chart */}
          {chartType === "line" && (
            <svg
              key="line-svg"
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              {/* Gradient definition */}
              <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="rgb(245, 158, 11)" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="rgb(245, 158, 11)" stopOpacity="0.05" />
                </linearGradient>
              </defs>
              {/* Area fill under line */}
              <motion.polygon
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                points={areaPoints}
                fill="url(#lineGradient)"
              />
              {/* Line */}
              <motion.polyline
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                points={linePoints}
                fill="none"
                stroke="rgb(245, 158, 11)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          )}

          {/* Bars / Dots container - key forces remount on chartType change for animations */}
          <div key={chartType} className="absolute inset-0 flex items-end">
            {metrics.map((m, i) => {
              const height = m.estimated_demand_index || 0;
              const isLow = m.is_low_season;

              return (
                <div key={m.month} className="flex-1 h-full relative group">
                  {chartType === "bar" ? (
                    // Bar visualization
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${height}%` }}
                      transition={{ delay: i * 0.04, duration: 0.5, ease: "easeOut" }}
                      className={cn(
                        "absolute bottom-0 left-1 right-1 rounded-t-md transition-colors",
                        isLow
                          ? "bg-red-500 group-hover:bg-red-400"
                          : m.demand_level === "high"
                            ? "bg-emerald-500 group-hover:bg-emerald-400"
                            : "bg-amber-500 group-hover:bg-amber-400"
                      )}
                    />
                  ) : (
                    // Dot for line chart
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: i * 0.04 + 0.4, duration: 0.3 }}
                      style={{ bottom: `${height}%` }}
                      className={cn(
                        "absolute left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white shadow-sm cursor-pointer transition-transform hover:scale-125",
                        isLow
                          ? "bg-red-500"
                          : m.demand_level === "high"
                            ? "bg-emerald-500"
                            : "bg-amber-500"
                      )}
                    />
                  )}
                  {/* Tooltip */}
                  <div
                    className="absolute left-1/2 -translate-x-1/2 px-2 py-1 rounded-lg bg-card border border-border shadow-lg text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20"
                    style={{ bottom: `calc(${height}% + 12px)` }}
                  >
                    <p className="font-medium">{monthNames[m.month - 1]} {m.year}</p>
                    <p className="text-muted-foreground">{m.estimated_demand_index?.toFixed(1)}%</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* X-axis labels */}
      <div className="flex ml-10 mt-2">
        {metrics.map((m) => (
          <div key={m.month} className="flex-1 text-center text-xs text-muted-foreground">
            {monthNames[m.month - 1]}
          </div>
        ))}
      </div>
    </div>
  );
}

function RegionHeatmap({
  regionHeatmap,
  onSelectRegion,
}: {
  regionHeatmap: Record<string, SeasonalityDashboardItem[]>;
  onSelectRegion: (region: string) => void;
}) {
  const regions = Object.keys(regionHeatmap).sort();

  if (regions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <MapPin className="h-12 w-12 opacity-30 mb-3" />
        <p>Seleziona una regione per vedere i dati</p>
      </div>
    );
  }

  return (
    <div className="space-y-1 max-h-[400px] overflow-y-auto">
      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2 sticky top-0 bg-card pb-1">
        <span className="w-24">Regione</span>
        {monthNames.map((m) => (
          <span key={m} className="flex-1 text-center">{m}</span>
        ))}
      </div>
      {regions.map((region) => {
        const data = regionHeatmap[region];
        return (
          <div
            key={region}
            className="flex items-center gap-1 cursor-pointer hover:bg-secondary/50 rounded p-1 transition-colors"
            onClick={() => onSelectRegion(region)}
          >
            <span className="w-24 text-xs truncate font-medium">{region}</span>
            {monthNames.map((_, i) => {
              const monthData = data.find(d => d.month === i + 1);
              const demand = monthData?.estimated_demand_index || 0;
              return (
                <div
                  key={i}
                  className={cn(
                    "flex-1 h-6 rounded-sm",
                    demand >= 70 && "bg-emerald-500/80",
                    demand >= 40 && demand < 70 && "bg-amber-500/80",
                    demand < 40 && "bg-red-500/80",
                    !monthData && "bg-secondary"
                  )}
                  title={monthData ? `${monthNames[i]}: ${demand.toFixed(0)}%` : "Nessun dato"}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function AlertCard({
  alert,
  onDismiss,
}: {
  alert: SeasonalityAlert;
  onDismiss: () => void;
}) {
  return (
    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium">{alert.title}</h4>
            <p className="text-xs text-muted-foreground mt-0.5">{alert.message}</p>
            {alert.period_start && (
              <p className="text-xs text-amber-600 mt-1">
                Periodo: {new Date(alert.period_start).toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
              </p>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-foreground"
          onClick={onDismiss}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function PreferencesModal({
  preferences,
  regions,
  onSave,
  onClose,
  isSaving,
}: {
  preferences: any;
  regions: string[];
  onSave: (updates: any) => void;
  onClose: () => void;
  isSaving: boolean;
}) {
  const [selectedRegions, setSelectedRegions] = useState<string[]>(
    preferences?.monitored_regions || []
  );
  const [alertThreshold, setAlertThreshold] = useState(
    preferences?.alert_threshold || 30
  );
  const [receiveAlerts, setReceiveAlerts] = useState(
    preferences?.receive_alerts ?? true
  );

  const handleToggleRegion = (region: string) => {
    setSelectedRegions(prev =>
      prev.includes(region)
        ? prev.filter(r => r !== region)
        : [...prev, region]
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Settings className="h-5 w-5 text-amber-500" />
            Preferenze Destagionalizzazione
          </h2>
        </div>

        <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
          {/* Alert Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-sm">Ricevi Avvisi</h3>
              <p className="text-xs text-muted-foreground">
                Notifiche per periodi a bassa domanda
              </p>
            </div>
            <button
              onClick={() => setReceiveAlerts(!receiveAlerts)}
              className={cn(
                "w-12 h-6 rounded-full transition-colors relative",
                receiveAlerts ? "bg-amber-500" : "bg-secondary"
              )}
            >
              <div
                className={cn(
                  "absolute top-1 w-4 h-4 rounded-full bg-white transition-transform",
                  receiveAlerts ? "translate-x-7" : "translate-x-1"
                )}
              />
            </button>
          </div>

          {/* Threshold */}
          <div>
            <h3 className="font-medium text-sm mb-2">Soglia Avviso</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Ricevi avvisi quando la domanda scende sotto questa soglia
            </p>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="10"
                max="50"
                value={alertThreshold}
                onChange={(e) => setAlertThreshold(Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm font-medium w-12">{alertThreshold}%</span>
            </div>
          </div>

          {/* Regions */}
          <div>
            <h3 className="font-medium text-sm mb-2">Regioni Monitorate</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Seleziona le regioni per cui vuoi ricevere avvisi
            </p>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
              {regions.map((region) => (
                <button
                  key={region}
                  onClick={() => handleToggleRegion(region)}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors",
                    selectedRegions.includes(region)
                      ? "bg-amber-500/20 text-amber-600 border border-amber-500/30"
                      : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
                  )}
                >
                  {selectedRegions.includes(region) ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <div className="w-4 h-4" />
                  )}
                  {region}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-border flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Annulla
          </Button>
          <Button
            className="flex-1"
            onClick={() => onSave({
              monitored_regions: selectedRegions,
              alert_threshold: alertThreshold,
              receive_alerts: receiveAlerts,
            })}
            disabled={isSaving}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Salva
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

